import { UsersApiService } from "../api/UsersService";
// import { GetCountryCodeDto } from "../api/index.defs";
import {
  IChangeCurrentUserPassword,
  IUpdateCurrentUserInfo
} from "../models/authenticated-user.interface";
import { KeyValue } from "../models/key-value";
import {
  AddOrganizationUserFailureReason,
  IAddOrganizationUserResult
} from "../models/results/add-org-user-result.interface";
import { IResult } from "../models/results/result.interface";
import Role from "../models/role";
import { IGetSupport } from "../models/support.interface";
import {
  IAddPatient,
  IAddUser,
  IOrgDomain,
  IUpdatePatient,
  IUpdateUser,
  IUser,
  OrgType
} from "../models/users.interface";
import {
  getPermissionsFromServerPermissions,
  getRoleFromServerRoleAndOrg,
  getServerRoleFromRole
} from "../utils/authorizationUtil";
import { apiDateFormatting } from "../utils/dateUtil";
import logger from "../utils/logger";
import {
  getAddOrganizationUserFailure,
  getBeltSizeFromServerBeltSize,
  getFullNameWithTitle,
  getGenderFromServerGender,
  getServerBeltSizeFromBeltSize,
  getServerGenderFromGender,
  getStatusFromServerStatus,
  getUserTitleFromServerTitle
} from "../utils/userUtil";

const getUsers: (roles?: Role[]) => Promise<IUser[] | null> = async (roles) => {
  logger.info(
    getUsers.name,
    `Getting users. Roles filter? ${roles?.map((role) => Role[role])}`
  );
  const serverRolesFilter = roles?.map((role) => getServerRoleFromRole(role));
  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersGetUsersExtended(
      serverRolesFilter
        ? {
          roleIds: serverRolesFilter,
        }
        : undefined
    );
    if (error || !resp) {
      logger.error(getUsers.name, "Invalid API response", { resp, error });
      return null;
    }

    return resp
      /**
* Server roles may contain legacy roles
* 1 = Patient (Legacy),
* 2 = Physician (Legacy),
* 3 = Nurse (Legacy),
* 4 = ApiUser (Legacy),
*/
      .filter((u) => u.roleId > 4)
      .map((apiUser) => ({
        id: apiUser.id,
        firstName: apiUser.firstName,
        lastName: apiUser.lastName,
        salutation: getUserTitleFromServerTitle(apiUser.salutation ?? ""),
        maxProfiles: apiUser.maxProfiles,
        createdAt: apiUser.dateTimeCreated ?? "",
        activatedAt: apiUser.dateTimeActivated ?? "",
        email: apiUser.userName,
        isActive: apiUser.enabled,
        status: getStatusFromServerStatus(apiUser.userStatus),
        phone: apiUser.phoneNumber ?? "",
        countryCode: apiUser.countryCode,
        organization: {
          id: apiUser.organization?.organizationId.toString() ?? "",
          name: apiUser.organization?.organizationName ?? "",
          type: apiUser.organization?.organizationType ?? OrgType.Undefined,
        },
        role: getRoleFromServerRoleAndOrg(
          apiUser.roleId,
          apiUser.organization?.organizationType
        ),
        permissions: getPermissionsFromServerPermissions({
          canViewEcgs: apiUser.canViewEcgs,
          canEditInterpretation: apiUser.canEditInterpretation,
          canManageProUsers: apiUser.canManageProUsers,
          canViewReports: apiUser.canViewReports,
          canLogIntoPortal: apiUser.canLogIntoPortal,
          canApproveEcg: apiUser.canApproveEcg,
          showEcgPreview: apiUser.showEcgPreview,
          canSendEcgsViaMail: apiUser.canSendEcgsViaMail,
          canViewSelfClinicsEcgs: apiUser.canViewSelfClinicsEcgs,
          canManageNonProUsers: apiUser.canManageNonProUsers,
          canArchiveEcgs: apiUser.canArchiveEcgs,
          isViewSelfClinicIsAllowed: apiUser.canViewSelfClinicsEcgs
        }),
        activeProfilesCount: apiUser.activeProfilesCount,
        totalProfiles: apiUser.totalProfilesCount,
        gender: getGenderFromServerGender(apiUser.gender as number),
        dateOfBirth: apiUser.dateOfBirth,
        lastEcgDate: apiUser.lastEcgDateTime,
        deviceSn: apiUser.deviceSN,
        beltSize: getBeltSizeFromServerBeltSize(apiUser.beltSize),
        emailVerification: apiUser.emailVerificationRequired,
        sendReportByEmail: apiUser.sendReportsByEmail,
        prescribingPhysician: apiUser.prescribingPhysician
          ? {
            id: apiUser.prescribingPhysician.id ?? "",
            firstName: apiUser.prescribingPhysician.firstName,
            lastName: apiUser.prescribingPhysician.lastName,
            title: apiUser.prescribingPhysician.salutation,
          }
          : undefined,
        totalEcgs: apiUser.totalECGsCount,
        lastLoginDate: apiUser.lastLoginDateTime,
        reference: undefined, // TODO: [API] Add reference (related to patient) once API is ready,
        hasPacemaker: apiUser.hasPacemaker
      }))
  } catch (error) {
    logger.error(getUsers.name, "Error getting users.", error);
    return null;
  }
};

const getPrescribingPhysicians = async (): Promise<KeyValue<string>[]> => {
  logger.info(
    getPrescribingPhysicians.name,
    "Getting Prescribing Physicians..."
  );
  try {
    const { response: users } = await UsersApiService.usersGetPhysicianUsers();

    return (
      users?.map((user) => {
        return {
          key: user.id ?? "",
          value: getFullNameWithTitle(
            user.salutation,
            user.firstName,
            user.lastName
          ),
        };
      }) ?? []
    );
  } catch (error) {
    logger.error(getPrescribingPhysicians.name, "Error getting users.", error);
    return [];
  }
};

const resetPassword = async (
  userId: string,
  password: string,
  isOneTimePassword: boolean
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(resetPassword.name, `updating password for user ${userId}`);

  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersResetUserPassword({
      userId,
      passwordDto: {
        password,
        isOneTimePassword,
      },
    });

    if (error || !resp) {
      logger.error(resetPassword.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(resetPassword.name, "Error updating password.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const addUser = async (user: IAddUser): Promise<IAddOrganizationUserResult> => {
  logger.info(addUser.name, `Adding new user`, user);

  const {
    entity,
    firstName,
    lastName,
    loginName,
    title,
    email,
    emailDomain,
    phone,
    // maxProfiles,
    canManageProUsers,
    canLogIntoPortal,
    canViewEcgs,
    canEditInterpretation,
    canManageNonProUsers,
    canViewReports,
    canApproveEcg,
    sendActivationEmail,
    countryCode,
    canSendEcgsViaMail,
    canViewSelfClinicsEcgs,
    canArchiveEcgs,
  } = user;

  const roleId = 8;

  const userName = emailDomain ? loginName + "@" + emailDomain : email;

  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersCreateOrganizationUser({
      user: {
        enabled: true,
        userName: userName,
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        salutation: title,
        roleId,
        password: "",
        phoneNumber: phone,
        distributorId: +entity,
        verifyEmail: false,
        // maxProfiles,
        canLogIntoPortal,
        canViewEcgs,
        canEditInterpretation,
        canManageNonProUsers,
        canViewReports,
        canApproveEcg,
        sendActivationEmail,
        countryCode,
        canManageProUsers,
        canSendEcgsViaMail,
        canViewSelfClinicsEcgs,
        canArchiveEcgs,
      },
    });

    if (error || !resp) {
      logger.error(addUser.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
        failReason: getAddOrganizationUserFailure(
          error?.errorCode?.toString() ?? "",
          roleId
        ),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(addUser.name, "Error adding user.", error);
    return {
      success: false,
      message: (error as Error).message,
      failReason: AddOrganizationUserFailureReason.Unknown,
    };
  }
};

const addPatient = async (
  patient: Omit<IAddPatient, "passwordConfirmation">
): Promise<IAddOrganizationUserResult> => {
  logger.info(addPatient.name, `Adding new patient`, patient);

  const patientRoleId = 7; // 7 = NonProfessionalOrganization
  const {
    entity,
    firstName,
    lastName,
    email,
    password,
    phone,
    dateOfBirth,
    gender,
    beltSize,
    // emailVerificationRequired,
    // sendReportByEmail,
    prescribingPhysician,
    reference,
    countryCode,
    canSendEcgsViaMail,
    canViewEcgs,
    showEcgPreview,
    hasPacemaker,
    isOneTimePassword
  } = patient;

  if (
    !email ||
    !entity ||
    !password ||
    !firstName ||
    !lastName ||
    !prescribingPhysician
  ) {
    throw new Error("Required data for adding patient is missing");
  }

  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersCreateOrganizationUser({
      user: {
        enabled: true,
        userName: email,
        firstName: firstName,
        lastName: lastName ?? "",
        roleId: patientRoleId,
        password: password,
        isOneTimePassword,
        phoneNumber: phone,
        distributorId: +entity,
        dob: dateOfBirth ? apiDateFormatting(dateOfBirth) : undefined,
        isMale: !gender ? undefined : gender === "Male",
        reference,
        beltSize: getServerBeltSizeFromBeltSize(beltSize),
        verifyEmail: false,
        sendReportsByEmail: false,
        prescribingPhysicianId: prescribingPhysician,
        countryCode,
        canSendEcgsViaMail,
        canViewEcgs,
        smartheartPreviewEnabled: showEcgPreview,
        pacemaker: hasPacemaker === "true"
      },
    });

    if (error || !resp) {
      logger.error(addPatient.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
        failReason: getAddOrganizationUserFailure(
          error?.errorCode?.toString() ?? "",
          patientRoleId
        ),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(addPatient.name, "Error adding patient.", error);
    return {
      success: false,
      message: (error as Error).message,
      failReason: AddOrganizationUserFailureReason.Unknown,
    };
  }
};

const updateUser = async (
  user: Omit<IUpdateUser, "loginName" | "emailDomain">
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(updateUser.name, `updating user`, user);

  const {
    id,
    firstName,
    lastName,
    email,
    title,
    phone,
    canEditInterpretation,
    canLogIntoPortal,
    canManageNonProUsers,
    canViewEcgs,
    canViewReports,
    canManageProUsers,
    entity,
    canApproveEcg,
    countryCode,
    canSendEcgsViaMail,
    canViewSelfClinicsEcgs,
    canArchiveEcgs
  } = user;

  try {
    const { response: resp, error } = await UsersApiService.usersUpdateUserEx({
      userId: id,
      user: {
        firstName,
        lastName,
        userName: email,
        canEditInterpretation,
        canManageNonProUsers,
        canViewEcgs,
        canViewReports,
        canApproveEcg,
        salutation: title,
        phoneNumber: phone,
        canLogIntoPortal,
        canManageProUsers,
        canSendEcgsViaMail,
        canViewSelfClinicsEcgs,
        entityId: entity ? +entity : undefined,
        countryCode,
        canArchiveEcgs
      },
    });

    if (error || !resp) {
      logger.error(updateUser.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(updateUser.name, "Error updating user.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const updatePatient = async (
  patient: IUpdatePatient
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(updatePatient.name, `updating patient`, patient);

  const {
    id,
    firstName,
    lastName,
    phone,
    entity,
    prescribingPhysician,
    dateOfBirth,
    beltSize,
    gender,
    reference,
    countryCode,
    canViewEcgs,
    canSendEcgsViaMail,
    showEcgPreview,
    hasPacemaker
  } = patient;
  try {
    const { response: resp, error } = await UsersApiService.usersUpdateUserEx({
      userId: id,
      user: {
        firstName,
        lastName,
        phoneNumber: phone,
        entityId: entity ? +entity : undefined,
        // beltSize: getServerBeltSizeFromBeltSize(beltSize), //
        // emailVerificationRequired: false,
        // sendEmails: false,
        prescribingPhysicianId: prescribingPhysician,
        dateOfBirth: dateOfBirth ? apiDateFormatting(dateOfBirth) : undefined,
        beltSize: beltSize
          ? getServerBeltSizeFromBeltSize(beltSize)
          : undefined,
        gender: gender ? getServerGenderFromGender(gender) : undefined,
        licenseNumber: reference,
        countryCode,
        canViewEcgs,
        canSendEcgsViaMail,
        smartheartPreviewEnabled: showEcgPreview,
        hasPacemaker: hasPacemaker ? hasPacemaker === "true" : undefined
      },
    });

    if (error || !resp) {
      logger.error(updatePatient.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(updatePatient.name, "Error updating patient.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const updateUserState = async (
  userId: string,
  isActive: boolean
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(
    updateUserState.name,
    isActive ? `Activating user ${userId}` : `Deactivating user ${userId}`
  );

  try {
    const { response: resp, error } = await UsersApiService.usersUpdateUserEx({
      userId,
      user: {
        enabled: isActive,
      },
    });

    if (error || !resp) {
      logger.error(updateUserState.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(
      updateUserState.name,
      isActive ? "Error Activating user." : "Error deactivating user.",
      error
    );
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getSupport = async ({ sender, subject, description }: IGetSupport) => {
  logger.info(getSupport.name, `Requesting support`);

  try {
    const { response: resp, error } = await UsersApiService.usersContactUs({
      contactUsDto: {
        body: description,
        subject,
        contactMeAt: sender,
      },
    });

    if (error || !resp) {
      logger.error(getSupport.name, "Invalid API response", {
        resp,
        error,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.error(getSupport.name, "Error requesting support.", error);
    return false;
  }
};

const getOrgEmailDomains = async (): Promise<IOrgDomain[] | null> => {
  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersGetOrganizationPermittedDomains();

    if (error || !resp) {
      logger.error(getOrgEmailDomains.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    const domains: IOrgDomain[] = resp.map((apiDomain) => {
      const { organizationId, domain } = apiDomain;

      const orgDomain: IOrgDomain = {
        id: organizationId,
        domain,
      };
      return orgDomain;
    });

    // TODO: [API] Remove check for "*" once API is ready
    const noDomainsExist = domains[0].domain === "*";

    return noDomainsExist ? null : domains;
  } catch (error) {
    logger.error(getOrgEmailDomains.name, "Error getting Org domains.", error);
    return null;
  }
};

const updateCurrentUserInfo = async (
  userInfo: IUpdateCurrentUserInfo
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(updateCurrentUserInfo.name, `Updating user's' info`);

  try {
    const { response: resp, error } = await UsersApiService.usersUpdateMe({
      updateMeDto: userInfo,
    });

    if (error || !resp) {
      logger.error(updateUserState.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(
      updateCurrentUserInfo.name,
      "Error updating user's info",
      error
    );
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const changeCurrentUserPassword = async (
  newPassword: IChangeCurrentUserPassword
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(changeCurrentUserPassword.name, `changing user's' password`);

  try {
    const { response: resp, error } = await UsersApiService.usersChangePassword(
      {
        passwordDto: newPassword,
      }
    );

    if (error || !resp) {
      logger.error(changeCurrentUserPassword.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(
      changeCurrentUserPassword.name,
      "Error changing user's password",
      error
    );
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

//UNCOMMENT IF COUNTRY CODES WILL BE SENT FROM SERVER
// const getCountryCodes = async (
// ): Promise<IResult<GetCountryCodeDto>> => {
//   logger.info(getCountryCodes.name, `getting country codes`);

//   try {
//     const { response: resp, error } = await UsersApiService.getCountryCodes();

//     if (error || !resp) {
//       logger.error(getCountryCodes.name, "Invalid API response", {
//         resp,
//         error,
//       });
//       return {
//         success: false,
//         message: error?.errorMessage,
//         errorCode: error?.errorCode?.toString(),
//       };
//     }

//     return {
//       success: true,
//       data: resp!
//     };
//   } catch (error) {
//     logger.error(
//       getCountryCodes.name,
//       "Error getting country codes",
//       error
//     );
//     return {
//       success: false,
//       message: (error as Error).message,
//     };
//   }
// };

const usersService = {
  getUsers,
  getPrescribingPhysicians,
  resetPassword,
  updateUser,
  updatePatient,
  addUser,
  addPatient,
  updateUserState,
  updateCurrentUserInfo,
  getSupport,
  getOrgEmailDomains,
  changeCurrentUserPassword,
  // getCountryCodes,
};

export default usersService;
